// src/features/dashboard/components/ManagerDashboard/IdleTimeReport/IdleFilters.tsx

import {
  useMemo,
  useState,
} from 'react';

import {
  Search,
  ChevronDown,
  X,
} from 'lucide-react';

import type {
  IdleTeamChartResponse,
} from '../../../types/Manager/idleTime.types';

interface Props {
  teamLeaders: IdleTeamChartResponse[];

  selectedTeam: string;

  onTeamChange: (
    value: string,
  ) => void;

  searchText: string;

  onSearchChange: (
    value: string,
  ) => void;
}

const IdleFilters = ({
  teamLeaders,
  selectedTeam,
  onTeamChange,
  searchText,
  onSearchChange,
}: Props) => {

  // =========================================================
  // State
  // =========================================================

  const [open, setOpen] =
    useState(false);

  const [teamSearch, setTeamSearch] =
    useState('');

  // =========================================================
  // Unique Team Leaders
  // =========================================================

  const uniqueTeamLeaders =
    useMemo(() => {

      const map = new Map<
        string,
        IdleTeamChartResponse
      >();

      (teamLeaders ?? []).forEach(
        (team) => {

          const code =
            String(
              team.teamLeadCode ?? '',
            ).trim();

          if (!code) {
            return;
          }

          if (!map.has(code)) {
            map.set(
              code,
              team,
            );
          }
        },
      );

      return Array.from(
        map.values(),
      );

    }, [
      teamLeaders,
    ]);

  // =========================================================
  // Search Team Leaders
  // =========================================================

  const filteredTeams =
    useMemo(() => {

      const search =
        teamSearch
          .trim()
          .toLowerCase();

      if (!search) {
        return uniqueTeamLeaders;
      }

      return uniqueTeamLeaders.filter(
        (team) => {

          const name =
            String(
              team.teamLeadName ?? '',
            ).toLowerCase();

          const code =
            String(
              team.teamLeadCode ?? '',
            ).toLowerCase();

          return (
            name.includes(search) ||
            code.includes(search)
          );
        },
      );

    }, [
      uniqueTeamLeaders,
      teamSearch,
    ]);

  // =========================================================
  // Selected Team Name
  // =========================================================

  const selectedTeamName =
    useMemo(() => {

      if (!selectedTeam) {
        return '';
      }

      const selected =
        uniqueTeamLeaders.find(
          (team) =>
            String(
              team.teamLeadCode ?? '',
            ).trim() ===
            String(
              selectedTeam,
            ).trim(),
        );

      return (
        selected?.teamLeadName ?? ''
      );

    }, [
      uniqueTeamLeaders,
      selectedTeam,
    ]);

  // =========================================================
  // Select Team
  // =========================================================

  const handleSelectTeam = (
    team: IdleTeamChartResponse,
  ) => {

    const code =
      String(
        team.teamLeadCode ?? '',
      ).trim();

    onTeamChange(code);

    setOpen(false);

    setTeamSearch('');
  };

  // =========================================================
  // Select All
  // =========================================================

  const handleSelectAll = () => {

    onTeamChange('');

    setOpen(false);

    setTeamSearch('');
  };

  // =========================================================
  // Clear Team
  // =========================================================

  const handleClearTeam = (
    event: React.MouseEvent,
  ) => {

    event.stopPropagation();

    onTeamChange('');

    setTeamSearch('');
  };

  // =========================================================
  // Clear Associate Search
  // =========================================================

  const handleClearSearch = () => {

    onSearchChange('');
  };

  // =========================================================
  // UI
  // =========================================================

  return (

    <div
      className="
        relative
        mt-4
        rounded-2xl
        border
        border-slate-200
        bg-white

        px-4
        py-3

        [@media(max-width:1100px)]:rounded-xl
        [@media(max-width:1100px)]:px-3
        [@media(max-width:1100px)]:py-2.5

        [@media(max-width:1000px)]:rounded-xl
        [@media(max-width:1000px)]:px-2.5
        [@media(max-width:1000px)]:py-2
      "
    >

      <div
        className="
          flex
          w-full
          flex-wrap
          items-center
          gap-3

          lg:flex-nowrap

          [@media(max-width:1100px)]:gap-2

          [@media(max-width:1000px)]:gap-1.5
        "
      >

        {/* =================================================
            TEAM LEADER DROPDOWN
        ================================================= */}

        <div
          className="
            relative
            w-[220px]
            shrink-0

            [@media(max-width:1100px)]:w-[190px]

            [@media(max-width:1000px)]:w-[175px]
          "
        >

          <button
            type="button"
            onClick={() =>
              setOpen(
                (previous) =>
                  !previous,
              )
            }
            className="
              flex
              h-10
              w-full
              items-center
              justify-between
              rounded-xl
              border
              border-slate-200
              bg-white
              px-3
              text-sm
              text-slate-700
              outline-none
              transition
              hover:border-blue-300
              focus:border-blue-500
              focus:ring-2
              focus:ring-blue-100

              [@media(max-width:1100px)]:h-9
              [@media(max-width:1100px)]:rounded-lg
              [@media(max-width:1100px)]:px-2.5
              [@media(max-width:1100px)]:text-[12px]

              [@media(max-width:1000px)]:h-8
              [@media(max-width:1000px)]:rounded-lg
              [@media(max-width:1000px)]:px-2
              [@media(max-width:1000px)]:text-[11px]
            "
          >

            <span
              className="
                min-w-0
                flex-1
                truncate
                text-left
              "
            >
              {selectedTeamName ||
                'All Team Leaders'}
            </span>

            <div
              className="
                ml-2
                flex
                shrink-0
                items-center
                gap-1
              "
            >

              {selectedTeam && (

                <span
                  role="button"
                  tabIndex={0}
                  onClick={
                    handleClearTeam
                  }
                  onKeyDown={(
                    event,
                  ) => {

                    if (
                      event.key ===
                        'Enter' ||
                      event.key ===
                        ' '
                    ) {

                      handleClearTeam(
                        event as unknown as React.MouseEvent,
                      );
                    }
                  }}
                  className="
                    rounded-full
                    p-0.5
                    text-slate-400
                    hover:bg-slate-100
                    hover:text-slate-700
                  "
                >
                  <X
                    size={14}
                  />
                </span>

              )}

              <ChevronDown
                size={16}
                className={`
                  transition-transform

                  ${
                    open
                      ? 'rotate-180'
                      : ''
                  }

                  [@media(max-width:1000px)]:h-[14px]
                  [@media(max-width:1000px)]:w-[14px]
                `}
              />

            </div>

          </button>


          {/* =================================================
              DROPDOWN
          ================================================= */}

          {open && (

            <div
              className="
                absolute
                left-0
                top-12
                z-[100]
                w-full
                overflow-hidden
                rounded-xl
                border
                border-slate-200
                bg-white
                shadow-xl

                [@media(max-width:1000px)]:top-10
                [@media(max-width:1000px)]:rounded-lg
              "
            >

              {/* =================================================
                  TEAM SEARCH
              ================================================= */}

              <div
                className="
                  border-b
                  border-slate-100
                  p-3

                  [@media(max-width:1100px)]:p-2

                  [@media(max-width:1000px)]:p-1.5
                "
              >

                <div
                  className="
                    relative
                  "
                >

                  <Search
                    size={15}
                    className="
                      absolute
                      left-3
                      top-1/2
                      -translate-y-1/2
                      text-slate-400

                      [@media(max-width:1000px)]:left-2
                      [@media(max-width:1000px)]:h-[13px]
                      [@media(max-width:1000px)]:w-[13px]
                    "
                  />

                  <input
                    type="text"
                    value={
                      teamSearch
                    }
                    onChange={(
                      event,
                    ) =>
                      setTeamSearch(
                        event.target
                          .value,
                      )
                    }
                    placeholder="
                      Search team leader...
                    "
                    autoFocus
                    className="
                      h-9
                      w-full
                      rounded-lg
                      border
                      border-slate-200
                      bg-white
                      pl-9
                      pr-3
                      text-sm
                      text-slate-700
                      outline-none
                      placeholder:text-slate-400
                      focus:border-blue-500
                      focus:ring-2
                      focus:ring-blue-100

                      [@media(max-width:1100px)]:h-8
                      [@media(max-width:1100px)]:pl-8
                      [@media(max-width:1100px)]:text-[11px]

                      [@media(max-width:1000px)]:h-7
                      [@media(max-width:1000px)]:pl-7
                      [@media(max-width:1000px)]:text-[10px]
                    "
                  />

                </div>

              </div>


              {/* =================================================
                  ALL TEAM LEADERS
              ================================================= */}

              <div
                className="
                  p-2

                  [@media(max-width:1000px)]:p-1.5
                "
              >

                <button
                  type="button"
                  onClick={
                    handleSelectAll
                  }
                  className={`
                    w-full
                    rounded-lg
                    px-3
                    py-2
                    text-left
                    text-sm
                    transition

                    [@media(max-width:1100px)]:px-2.5
                    [@media(max-width:1100px)]:py-1.5
                    [@media(max-width:1100px)]:text-[11px]

                    [@media(max-width:1000px)]:px-2
                    [@media(max-width:1000px)]:py-1.5
                    [@media(max-width:1000px)]:text-[10px]

                    ${
                      !selectedTeam
                        ? `
                          bg-blue-50
                          font-semibold
                          text-blue-700
                        `
                        : `
                          text-slate-700
                          hover:bg-slate-50
                        `
                    }
                  `}
                >
                  All Team Leaders
                </button>

              </div>


              {/* =================================================
                  TEAM LIST
              ================================================= */}

              <div
                className="
                  max-h-60
                  overflow-y-auto
                  px-2
                  pb-2

                  [@media(max-width:1000px)]:px-1.5
                  [@media(max-width:1000px)]:pb-1.5
                "
              >

                {filteredTeams.map(
                  (team) => {

                    const teamCode =
                      String(
                        team.teamLeadCode ??
                          '',
                      ).trim();

                    const teamName =
                      String(
                        team.teamLeadName ??
                          '',
                      ).trim();

                    const isSelected =
                      teamCode ===
                      String(
                        selectedTeam,
                      ).trim();

                    return (

                      <button
                        key={
                          teamCode
                        }
                        type="button"
                        onClick={() =>
                          handleSelectTeam(
                            team,
                          )
                        }
                        className={`
                          block
                          w-full
                          rounded-lg
                          px-3
                          py-2.5
                          text-left
                          transition

                          [@media(max-width:1100px)]:px-2.5
                          [@media(max-width:1100px)]:py-2

                          [@media(max-width:1000px)]:px-2
                          [@media(max-width:1000px)]:py-1.5

                          ${
                            isSelected
                              ? `
                                bg-blue-50
                                text-blue-700
                              `
                              : `
                                text-slate-700
                                hover:bg-slate-50
                              `
                          }
                        `}
                      >

                        <div
                          className="
                            truncate
                            text-sm
                            font-medium

                            [@media(max-width:1100px)]:text-[11px]

                            [@media(max-width:1000px)]:text-[10px]
                          "
                        >
                          {teamName ||
                            'Unknown Team Leader'}
                        </div>

                        <div
                          className="
                            mt-0.5
                            text-[10px]
                            text-slate-400

                            [@media(max-width:1000px)]:text-[9px]
                          "
                        >
                          {teamCode}
                        </div>

                      </button>
                    );
                  },
                )}


                {filteredTeams.length ===
                  0 && (

                  <div
                    className="
                      px-3
                      py-5
                      text-center
                      text-sm
                      text-slate-500

                      [@media(max-width:1000px)]:px-2
                      [@media(max-width:1000px)]:py-4
                      [@media(max-width:1000px)]:text-[10px]
                    "
                  >
                    No Team Leaders Found
                  </div>

                )}

              </div>

            </div>

          )}

        </div>


        {/* =================================================
            ASSOCIATE SEARCH
        ================================================= */}

        <div
          className="
            relative
            w-[260px]
            shrink-0

            [@media(max-width:1100px)]:w-[220px]

            [@media(max-width:1000px)]:w-[195px]
          "
        >

          <Search
            size={16}
            className="
              absolute
              left-3
              top-1/2
              -translate-y-1/2
              text-slate-400

              [@media(max-width:1000px)]:left-2.5
              [@media(max-width:1000px)]:h-[14px]
              [@media(max-width:1000px)]:w-[14px]
            "
          />

          <input
            type="text"
            value={searchText}
            onChange={(event) =>
              onSearchChange(
                event.target.value,
              )
            }
            placeholder="Search associate..."
            className="
              h-10
              w-full
              rounded-xl
              border
              border-slate-200
              bg-white
              pl-10
              pr-10
              text-sm
              text-slate-700
              outline-none
              transition
              placeholder:text-slate-400
              focus:border-blue-500
              focus:ring-2
              focus:ring-blue-100

              [@media(max-width:1100px)]:h-9
              [@media(max-width:1100px)]:rounded-lg
              [@media(max-width:1100px)]:pl-9
              [@media(max-width:1100px)]:pr-9
              [@media(max-width:1100px)]:text-[12px]

              [@media(max-width:1000px)]:h-8
              [@media(max-width:1000px)]:pl-8
              [@media(max-width:1000px)]:pr-8
              [@media(max-width:1000px)]:text-[11px]
            "
          />

          {searchText && (

            <button
              type="button"
              onClick={
                handleClearSearch
              }
              className="
                absolute
                right-3
                top-1/2
                -translate-y-1/2
                rounded-full
                p-0.5
                text-slate-400
                hover:bg-slate-100
                hover:text-slate-700

                [@media(max-width:1000px)]:right-2
              "
            >
              <X
                size={15}
                className="
                  [@media(max-width:1000px)]:h-[13px]
                  [@media(max-width:1000px)]:w-[13px]
                "
              />
            </button>

          )}

        </div>


        {/* =================================================
            SELECTED TEAM BADGE
        ================================================= */}

        {selectedTeam &&
          selectedTeamName && (

          <div
            className="
              flex
              h-8
              min-w-0
              max-w-[260px]
              items-center
              gap-1.5
              overflow-hidden
              rounded-full
              bg-blue-50
              px-3
              text-xs
              font-medium
              text-blue-700

              [@media(max-width:1100px)]:h-7
              [@media(max-width:1100px)]:max-w-[220px]
              [@media(max-width:1100px)]:gap-1
              [@media(max-width:1100px)]:px-2.5
              [@media(max-width:1100px)]:text-[11px]

              [@media(max-width:1000px)]:h-6
              [@media(max-width:1000px)]:max-w-[190px]
              [@media(max-width:1000px)]:px-2
              [@media(max-width:1000px)]:text-[10px]
            "
          >

            <span
              className="
                min-w-0
                truncate
              "
            >
              Team:{' '}
              {selectedTeamName}
            </span>

            <button
              type="button"
              onClick={() =>
                onTeamChange('')
              }
              className="
                shrink-0
                rounded-full
                p-0.5
                hover:bg-blue-100
              "
            >
              <X
                size={13}
                className="
                  [@media(max-width:1000px)]:h-[11px]
                  [@media(max-width:1000px)]:w-[11px]
                "
              />
            </button>

          </div>

        )}

      </div>

    </div>
  );
};

export default IdleFilters;